import Big from 'big.js'

import {
  CREDIT_NOTE_LABEL,
  isGrantApplied,
  toAppliedCreditNoteAmount,
  toCreditAppliedLabel,
  toGrantNetAmount,
  toSettlementTotals
} from '@sequencehq/invoice-content'
import { AppliedCreditGrant, AppliedCreditNote } from 'Invoices/types'

export type InvoiceSettlement = {
  creditNoteRows: InvoiceSettlementRow[]
  creditGrantRows: InvoiceSettlementRow[]
  adjustmentNetTotal: string
  adjustmentTotalTax: string
  adjustmentGrossTotal: string
}

type InvoiceSettlementRow = {
  key: string
  label: string
  amount: string
  /**
   * Dashboard link target for the row's source document. Credit notes link to
   * their page; credit grants have no detail page, so those rows are plain text.
   */
  href?: string
  /**
   * Explicit `data-testid` for rows whose label is unsuitable as a selector.
   * Grant labels embed a merchant-authored name, and two unattributed grants
   * would share one label. Rows without it fall back to the label-derived
   * default.
   */
  testId?: string
}

/**
 * Settlement rows below the invoice totals. Grants form the subtotal and tax;
 * tax-inclusive credit note applications enter after tax and before the total.
 *
 *   subtotal:       applied grant amounts
 *   tax:            applied grant tax
 *   total:          grant subtotal + tax + credit note applications
 */
export const toInvoiceSettlement = ({
  appliedCreditNotes,
  appliedCreditGrants,
  appliedCreditGrantTax
}: {
  appliedCreditNotes: AppliedCreditNote[]
  appliedCreditGrants: AppliedCreditGrant[]
  appliedCreditGrantTax?: string | null
}): InvoiceSettlement => {
  // The one place grants enter this surface: anything not APPLIED is dropped
  // here, so it reaches neither a row nor a total.
  const grants = appliedCreditGrants.filter(isGrantApplied)

  const creditNoteRows = appliedCreditNotes.map((creditNote, index) => ({
    // The API can return multiple applications of the same credit note.
    key: `${creditNote.id}-${index}`,
    // `||` rather than `??` so an empty-string number also falls back to the
    // generic label instead of rendering a blank row, matching the renderers.
    label: creditNote.creditNoteNumber || CREDIT_NOTE_LABEL,
    amount: negate(toAppliedCreditNoteAmount(creditNote)),
    href: `/credit-notes/${creditNote.id}`
  }))

  const creditGrantRows = grants.map((grant, index) => {
    // The index keeps unattributed credit rows keyed uniquely when the grant
    // carries no id.
    const key = grant.creditGrantId ?? `credit-grant-${index}`

    return {
      key,
      testId: `Totals.creditGrant.${key}`,
      label: toCreditAppliedLabel(grant.creditGrantName),
      amount: negate(toGrantNetAmount(grant))
    }
  })

  const adjustment = toSettlementTotals({
    appliedCreditNotes,
    appliedCreditGrants: grants,
    appliedCreditGrantTax
  })

  return {
    creditNoteRows,
    creditGrantRows,
    adjustmentNetTotal: negate(adjustment.net),
    adjustmentTotalTax: negate(adjustment.tax),
    adjustmentGrossTotal: negate(adjustment.gross)
  }
}

// Big normalises -0 to 0, so a zero amount renders as "$0.00" rather than "-$0.00".
// Exported for other settlement displays (e.g. the issue credit note drawer).
export const negate = (value: string): string =>
  new Big(value).times(-1).toString()
